import React, {useEffect} from 'react';
import {LoadingSmall} from 'Components/Tailwind/Button';
import {Typography} from 'Components/Typography';
import useSWR from 'swr';
import {formatDistance} from 'date-fns';
import {BaseButton} from './BaseButton';

interface Props<Data, Args> {
    page: number;
    setShouldDisablePrev: (shouldDisable: boolean) => void;
    setShouldDisableNext: (shouldDisable: boolean) => void;
    fetcher: (key: Args) => Promise<Data[]>;
    args: Args;
    idGetter: (data: Data) => string;
    imageGetter: (data: Data) => string | undefined;
    dateGetter: (data: Data) => number | Date | undefined;
    linkGetter: (data: Data) => string | undefined;
}

export const HistoryList = <T, Args extends Object>({
    page,
    setShouldDisablePrev,
    setShouldDisableNext,
    fetcher,
    args,
    idGetter,
    imageGetter,
    dateGetter,
    linkGetter,
}: Props<T, Args>) => {
    const {data, error, isLoading} = useSWR(args, fetcher, {
        errorRetryCount: 2,
    });

    useEffect(() => {
        if (page === 0 || !!error || isLoading) {
            setShouldDisablePrev(true);
        } else {
            setShouldDisablePrev(false);
        }

        if (isLoading || data?.length === 0 || (!isLoading && !!data?.length && data.length < 10)) {
            setShouldDisableNext(true);
        } else {
            setShouldDisableNext(false);
        }

        if (error) {
            setShouldDisablePrev(true);
            setShouldDisableNext(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.length, error, isLoading, page, setShouldDisableNext, setShouldDisablePrev, args]);

    return (
        <div className="flex w-full flex-grow flex-col">
            {!isLoading && error && (
                <div className="mb-8 flex h-20 w-full flex-row items-center justify-center rounded-lg border border-red-500 bg-red-500/30 p-4">
                    <Typography.P className="text-red-500">
                        {data && data.length
                            ? 'Something went wrong updating history'
                            : 'Something went wrong loading history'}
                    </Typography.P>
                </div>
            )}
            {isLoading && <LoadingSmall className="my-auto" />}
            {!isLoading && data && data.length > 0 && (
                <div className="grid w-full grid-cols-2 grid-rows-[repeat(5,_minmax(0,_230px))] gap-4 gap-y-8 lg:grid-cols-5 lg:grid-rows-[repeat(2,_minmax(0,_230px))]">
                    {data?.map((d) => {
                        const date = dateGetter(d);
                        return (
                            <div key={idGetter(d)} className="flex h-[250px] w-full flex-col">
                                <div
                                    key={idGetter(d)}
                                    className="flex h-40 w-full items-center justify-center rounded-t-xl border border-[#E6E6E6]"
                                >
                                    {imageGetter(d) ? (
                                        <img
                                            className="h-full w-full object-contain"
                                            src={imageGetter(d)}
                                            alt="Compile Preview"
                                        />
                                    ) : (
                                        <img
                                            className="h-full w-full object-contain"
                                            src="https://file.rendit.io/n/p.png"
                                            alt="Preview Placeholder"
                                        />
                                    )}
                                </div>
                                <div className="flex w-full flex-row items-center justify-between rounded-b-xl border-b border-l border-r border-[#E6E6E6] p-4">
                                    <Typography.P className="truncate">
                                        {date
                                            ? `${formatDistance(date, new Date())} ago`
                                            : 'Created at the dawn of time...'}
                                    </Typography.P>
                                    {linkGetter(d) && (
                                        <BaseButton onClick={() => window.open(linkGetter(d))}>View</BaseButton>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {!isLoading && data?.length === 0 && (
                <div className="m-auto flex w-full flex-grow items-center justify-center">
                    Nothing here yet! Head back to figma to get started.
                </div>
            )}
        </div>
    );
};
